import * as Tone from 'tone';
import { Sampler, SamplerOptions } from 'tone';
import { Instrument, InstrumentOptions } from 'tone/build/esm/instrument/Instrument';
import { MalakaiInstrument } from '../../types';
import { PUBLIC_SAMPLES_PATH } from '../../../constants';

// SamplerOptions Reference: https://tonejs.github.io/docs/14.7.77/interface/SamplerOptions

function generateMalakaiSampler(
  samplerOptions: Partial<SamplerOptions>,
  meta: Partial<MalakaiInstrument>,
  pipe?: (sampler: Sampler) => Sampler,
): MalakaiInstrument {
  return {
    instrument: () => new Promise<Instrument<InstrumentOptions>>((resolve, reject) => {
      const sampler = new Tone.Sampler({
        ...samplerOptions,
        onload: () => resolve(pipe ? pipe(sampler) : sampler),
        onerror: reject,
      });
    }),
    electronic: 0,
    ...meta,
  };
}

export const Metronome = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Metronome/`,
  urls: {
    C5: 'C5.wav',
  },
  volume: -25,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

/// Perc
export const FonoboKick = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Kicks/`,
  urls: {
    A2: 'FonoboKick.m4a',
  },
  volume: -4,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const PercStormOrch = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Perc_StormOrch/`,
  urls: {
    A2: 'Timpani_A.m4a',
  },
  volume: -5,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

export const Perc80sKit = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Perc_80sKit/`,
  urls: {
    B2: 'synthwaveSnare.m4a',
    A2: 'synthwaveKick.m4a',
  },
  volume: 9,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});
/// Hat Loops
export const LoopsHats = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Loops_Hats/`,
  urls: {
    A2: '80hatloop96.m4a',
  },
  volume: 7,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

/// Clap Loops
export const FonoboClap = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Loops_Claps/`,
  urls: {
    A2: 'FonoboClap112.m4a',
  },
  volume: -3,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

/// Perc Loops
export const FonoboPerc = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Loops_Perc/`,
  urls: {
    A2: 'GlasperPercLoop112.m4a',
  },
  volume: 3,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

/// Bass Synthetic

export const BassFatimaHugeSynth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_FatimaHugeSynth/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 2,
  attack: 0,
  release: 0.5,
},
{
  electronic: 1,
});

export const BassFatZimmerSynth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_FatZimmerSynth/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 15,
  attack: 0,
  release: 0.5,
},
{
  electronic: 1,
});

export const BassFatZimmerAndSub = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_FatZimmerAndSub/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 10,
  attack: 0,
  release: 0.5,
},
{
  electronic: 1,
});

export const BassSubduedSubbySaw = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_SubduedSubbySaw/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 5,
  attack: 0,
  release: 0.5,
},
{
  electronic: 1,
});
/// Bass Tmatch

export const BassSynthwave96BPM = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_Synthwave_96BPM/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 18,
  attack: 0,
  release: 0.5,
},
{
  electronic: 1,
});

export const BassFonobo = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_FonoboSC112/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 20,
  attack: 0,
  release: 0.5,
},
{
  electronic: 1,
});

/// Bass Organic

export const BassCleanPickedFender = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_CleanPickedFender/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 5,
  attack: 0,
  release: 0.5,
},
{
  electronic: 0,
});

export const BassPlainFretless = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Bass_PlainFretless/`,
  urls: {
    A3: 'A2.m4a',
    B3: 'B2.m4a',
    C3: 'C2.m4a',
    D3: 'D2.m4a',
    E3: 'E2.m4a',
    F3: 'F2.m4a',
    G3: 'G2.m4a',
  },
  volume: 0,
  attack: 0,
  release: 0.5,
},
{
  electronic: 0,
});

/// Keys

/// Keys Synth

export const KeysDreamySpaceOrganSynth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_DreamySpaceOrganSynth/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 0,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const KeysFonoboPlucks = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_FonoboPlucks_112BPM/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    A5: 'A4.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 18,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const Keys1982Synth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_1982Synth/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 0,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const Keys1982OceanSynth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_1982OceanSynth/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: -5,
  attack: 0,
  release: 2,
},
{
  electronic: 1,
});

export const KeysIverChoir = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_IverChoir/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 6,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const KeysDeepChorusedBrass = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_DeepChorusedBrass/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 5,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const EnoSynth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/EnoSynth/`,
  urls: {
    A1: 'A1.m4a',
    A2: 'A2.m4a',
    A3: 'A3.m4a',
    A4: 'A4.m4a',
    B3: 'B3.m4a',
    C2: 'C2.m4a',
    C3: 'C3.m4a',
    C4: 'C4.m4a',
    D2: 'D2.m4a',
    D3: 'D3.m4a',
    D4: 'D4.m4a',
    E2: 'E2.m4a',
    E3: 'E3.m4a',
    E4: 'E4.m4a',
    F2: 'F2.m4a',
    F3: 'F3.m4a',
    F4: 'F4.m4a',
    G2: 'G2.m4a',
    G3: 'G3.m4a',
    G4: 'G4.m4a',
  },
  volume: -6,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

/// Keys Organic

export const KeysImmunityPiano = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_ImmunityPiano/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 5,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

export const KeysImmesurablePiano = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_ImmesurablePiano/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 5,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

export const KeysHangDrumEcho = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_HangDrumEcho/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 10,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

export const KeysEpicGospelOrgan = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_EpicGospelOrgan/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 9,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

export const KeysFlamencoGuitar = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Keys_FlamencoGuitar/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 0,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

export const WarmSmokyPiano = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/WarmSmokyPiano/`,
  urls: {
    A1: 'A1.m4a',
    A2: 'A2.m4a',
    A4: 'A4.m4a',
    C2: 'C2.m4a',
    C3: 'C3.m4a',
    C4: 'C4.m4a',
    D2: 'D2.m4a',
    E3: 'E3.m4a',
    E4: 'E4.m4a',
    F2: 'F2.m4a',
    G3: 'G3.m4a',
  },
  volume: -15,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

/// leads synth

export const LeadSimpleBladeSynth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Lead_SimpleBladeSynth/`,
  urls: {
    A4: 'A3.m4a',
    A5: 'A4.m4a',
    B4: 'B3.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: -15,
  attack: 0,
  release: 2,
},
{
  electronic: 1,
});

/// leads organic
export const LeadFractPiano = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Lead_FractPiano/`,
  urls: {
    A3: 'A3.m4a',
    A4: 'A4.m4a',
    B3: 'B3.m4a',
    B4: 'B4.m4a',
    C3: 'C3.m4a',
    C4: 'C4.m4a',
    C5: 'C5.m4a',
    D3: 'D3.m4a',
    D4: 'D4.m4a',
    E3: 'E3.m4a',
    E4: 'E4.m4a',
    F3: 'F3.m4a',
    F4: 'F4.m4a',
    G3: 'G3.m4a',
    G4: 'G4.m4a',
  },
  volume: -20,
  attack: 0,
  release: 4,
},
{
  electronic: 0,
});

export const LeadShakahuchiFlute = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Lead_ShakahuchiFlute/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    A5: 'A4.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: -15,
  attack: 0.5,
  release: 2,
},
{
  electronic: 0,
});

export const LeadImmesurablePiano = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Lead_ImmesurablePiano/`,
  urls: {
    A2: 'A2.m4a',
    A3: 'A3.m4a',
    B2: 'B2.m4a',
    B3: 'B3.m4a',
    C2: 'C2.m4a',
    C3: 'C3.m4a',
    C4: 'C4.m4a',
    D2: 'D2.m4a',
    D3: 'D3.m4a',
    D4: 'D4.m4a',
    E2: 'E2.m4a',
    E3: 'E3.m4a',
    E4: 'E4.m4a',
    F2: 'F2.m4a',
    F3: 'F3.m4a',
    F4: 'F4.m4a',
    G2: 'G2.m4a',
    G3: 'G3.m4a',
    G4: 'G4.m4a',
  },
  volume: 2,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

/// Pads Synth

export const PadSyntheticOrchMovement = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Pad_SyntheticOrchMovement/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: -3,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const PadMorningDreamsSynth = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Pad_MorningDreamsSynth/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: 10,
  attack: 0,
  release: 1,
},
{
  electronic: 1,
});

export const DirtyAirPad = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/DirtyAirPad/`,
  urls: {
    A3: 'A3.m4a',
    A4: 'A4.m4a',
    B3: 'B3.m4a',
    B4: 'B4.m4a',
    C4: 'C4.m4a',
    C5: 'C5.m4a',
    D4: 'D4.m4a',
    E4: 'E4.m4a',
    F4: 'F4.m4a',
    G4: 'G4.m4a',
  },
  volume: -17,
  attack: 0,
  release: 0.5,
},
{
  electronic: 1,
});

/// Pads Organic

export const PadWindyGuitaret = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Pad_WindyGuitaret/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: -5,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

export const PadYouthChoirSoftMm = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Pad_YouthChoirSoftMm/`,
  urls: {
    A3: 'A2.m4a',
    A4: 'A3.m4a',
    B3: 'B2.m4a',
    B4: 'B3.m4a',
    C3: 'C2.m4a',
    C4: 'C3.m4a',
    C5: 'C4.m4a',
    D3: 'D2.m4a',
    D4: 'D3.m4a',
    D5: 'D4.m4a',
    E3: 'E2.m4a',
    E4: 'E3.m4a',
    E5: 'E4.m4a',
    F3: 'F2.m4a',
    F4: 'F3.m4a',
    F5: 'F4.m4a',
    G3: 'G2.m4a',
    G4: 'G3.m4a',
    G5: 'G4.m4a',
  },
  volume: -8,
  attack: 0,
  release: 1,
},
{
  electronic: 0,
});

/// sparkles
export const SparkEternalVox = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Spark_EternalVox/`,
  urls: {
    A3: 'A3.m4a',
    A4: 'A4.m4a',
    B3: 'B3.m4a',
    B4: 'B4.m4a',
    C3: 'C3.m4a',
    C4: 'C4.m4a',
    C5: 'C5.m4a',
    D3: 'D3.m4a',
    D4: 'D4.m4a',
    E3: 'E3.m4a',
    E4: 'E4.m4a',
    F3: 'F3.m4a',
    F4: 'F4.m4a',
    G3: 'G3.m4a',
    G4: 'G4.m4a',
  },
  volume: -5,
  attack: 1,
  release: 8,
},
{
  electronic: 0,
});

export const SparkFonoboMoans = generateMalakaiSampler({
  baseUrl: `${PUBLIC_SAMPLES_PATH}/Spark_FonoboMoans/`,
  urls: {
    A3: '1.m4a',
    B3: '2.m4a',
    C3: '3.m4a',
    D3: '4.m4a',
    E3: '5.m4a',
    F3: '6.m4a',
    G3: '7.m4a',
    A4: '8.m4a',
  },
  volume: 15,
  attack: 1,
  release: 8,
},
{
  electronic: 0,
});
